import React, { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import Input from "../../../../../Components/Common/Input";
import HtmlEditor from "../../../../../Components/Common/HtmlEditor";
import ImageSelector from "../../../../../Components/Common/ImageSelector";
import Button from "../../../../../Components/Common/Button";
import Modal from "../../../../../Components/Common/Modal";
import Storage from "../../../Storage";
import { toast } from "react-toastify";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
  fetcher,
} from "../../../../../Api/swrConfig";
import { StaticPageApiUrls } from "../../../../../Api/Auth/ApiUrls";
import useToastError from "../../../../../hooks/useErrorToast";

const DEFAULT_FORM_STATE = {
  title: "",
  slug: "",
  heroTitle: "",
  heroSubtitle: "",
  backgroundImage: "",
  bodyContent: "",
};

const StaticContactForm = ({
  title,
  customSlug,
  includeSocialLinks = true,
}) => {
  const generateSlug = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/[\s]+/g, "-")
      .replace(/[^\w-]+/g, "");

  const { showErrorToast } = useToastError();
  const derivedSlug = generateSlug(title || "");

  const initialState = useMemo(() => {
    const baseState = {
      ...DEFAULT_FORM_STATE,
      title: title || "",
      slug: customSlug || derivedSlug,
    };

    if (includeSocialLinks) {
      baseState.linkedin = "";
      baseState.facebook = "";
      baseState.youtube = "";
    }

    return baseState;
  }, [customSlug, derivedSlug, includeSocialLinks, title]);

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [imageField, setImageField] = useState(null);

  const { data } = useSWR(
    formData.slug ? StaticPageApiUrls.info(formData.slug) : null,
    fetcher
  );

  const parsedContent = useMemo(() => {
    if (!data?.data?.content) return {};
    try {
      return JSON.parse(data.data.content);
    } catch (error) {
      console.warn("Failed to parse contact page content", error);
      return {};
    }
  }, [data]);

  useEffect(() => {
    if (!data?.data) return;

    const pageData = data.data;
    const socialLinks = parsedContent?.socialLinks || parsedContent || {};

    setFormData((prev) => ({
      ...prev,
      title: pageData.title || title || "",
      slug: pageData.slug || customSlug || derivedSlug,
      heroTitle:
        parsedContent?.heroTitle ||
        pageData.title ||
        pageData.displayTitle ||
        prev.heroTitle,
      heroSubtitle:
        parsedContent?.heroSubtitle ||
        pageData.headerDescription ||
        parsedContent?.subtitle ||
        prev.heroSubtitle,
      backgroundImage:
        parsedContent?.backgroundImage ||
        parsedContent?.heroImage ||
        pageData.image ||
        prev.backgroundImage,
      bodyContent:
        parsedContent?.bodyContent ||
        parsedContent?.content ||
        pageData.bodyDescription ||
        prev.bodyContent,
      ...(includeSocialLinks
        ? {
            linkedin: socialLinks?.linkedin || "",
            facebook: socialLinks?.facebook || "",
            youtube: socialLinks?.youtube || "",
          }
        : {}),
    }));
  }, [customSlug, data, derivedSlug, includeSocialLinks, parsedContent, title]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title?.trim()) newErrors.title = "Title is required";
    if (!formData.slug?.trim()) newErrors.slug = "Slug is required";
    if (!formData.heroTitle?.trim())
      newErrors.heroTitle = "Hero title is required";
    if (!formData.heroSubtitle?.trim())
      newErrors.heroSubtitle = "Hero subtitle is required";
    if (!formData.bodyContent?.trim())
      newErrors.bodyContent = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "title" && !customSlug) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleImageSelectorChange = (name, value) => {
    const normalized = Array.isArray(value) ? value[0] || "" : value || "";
    setFormData((prev) => ({ ...prev, [name]: normalized }));
  };

  const buildPayload = () => ({
    title: formData.title.trim(),
    content: JSON.stringify({
      heroTitle: formData.heroTitle,
      heroSubtitle: formData.heroSubtitle,
      backgroundImage: formData.backgroundImage,
      bodyContent: formData.bodyContent,
      ...(includeSocialLinks
        ? {
            socialLinks: {
              linkedin: formData.linkedin,
              facebook: formData.facebook,
              youtube: formData.youtube,
            },
          }
        : {}),
      isStaticPage: false,
    }),
  });

  const mutationOptions = {
    onSuccess: () => {
      toast.success("Success");
    },
    onError: (error) => {
      showErrorToast(error);
    },
  };

  const { trigger: editTrigger, isMutating } = useSWRMutation(
    StaticPageApiUrls.edit(formData.slug),
    sendPutRequestAuth,
    mutationOptions
  );

  const { trigger: createTrigger, isMutating: createMutating } = useSWRMutation(
    StaticPageApiUrls.create,
    sendPostRequestAuth,
    mutationOptions
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = buildPayload();

    try {
      await editTrigger(payload);
    } catch (error) {
      if (error.response?.data?.message === "Static Page not found.") {
        await createTrigger({ ...payload, slug: formData.slug });
      }
    }
  };

  return (
    <div className="mt-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label="Page title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          error={errors.title}
          required
          placeholder="Enter page title"
          disabled={Boolean(title)}
        />
        <Input
          label="Page slug"
          name="slug"
          value={formData.slug}
          onChange={handleInputChange}
          error={errors.slug}
          required
          placeholder="Enter page slug"
          disabled
        />
        <Input
          label="Page Title (visible)"
          name="heroTitle"
          value={formData.heroTitle}
          onChange={handleInputChange}
          error={errors.heroTitle}
          placeholder="Enter page title"
        />
        <Input
          label="Page Subtitle (visible)"
          name="heroSubtitle"
          value={formData.heroSubtitle}
          onChange={handleInputChange}
          error={errors.heroSubtitle}
          placeholder="Enter page subtitle"
        />

        <ImageSelector
          label="Background image"
          name="backgroundImage"
          value={formData.backgroundImage}
          setModalOpen={setModalOpen}
          onClick={() => setImageField("backgroundImage")}
          onChange={handleImageSelectorChange}
        />

        <HtmlEditor
          label="Description"
          name="bodyContent"
          value={formData.bodyContent}
          onChange={handleInputChange}
          error={errors.bodyContent}
          placeholder="Add page description"
        />

        {includeSocialLinks && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="LinkedIn URL"
              name="linkedin"
              value={formData.linkedin}
              onChange={handleInputChange}
              placeholder="https://linkedin.com/company/roadtope"
            />
            <Input
              label="Facebook URL"
              name="facebook"
              value={formData.facebook}
              onChange={handleInputChange}
              placeholder="https://facebook.com/roadtope"
            />
            <Input
              label="YouTube URL"
              name="youtube"
              value={formData.youtube}
              onChange={handleInputChange}
              placeholder="https://youtube.com/@roadtope"
            />
          </div>
        )}

        <div className="flex justify-end gap-4">
          <Button
            type="primary"
            size="small"
            isLoading={isMutating || createMutating}
          >
            Confirm
          </Button>
        </div>
      </form>

      <Modal
        isOpen={isModalOpen}
        title="Storage Manager"
        onCancel={() => {
          setModalOpen(false);
          setImageField(null);
        }}
        footer={false}
      >
        <Storage
          isSelector
          onConfirm={(file) => {
            if (Array.isArray(file) && file.length && imageField) {
              handleImageSelectorChange(imageField, file[0]?.fileUrl);
            }
            setModalOpen(false);
            setImageField(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default StaticContactForm;

